import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';

import { fetchMe as fetchMeApi } from '#/api/auth/fetchMe';
import { login as loginApi } from '#/api/auth/login';
import { logout as logoutApi } from '#/api/auth/logout';
import {
  exitToLogin,
  resetSessionExitFlag,
  SESSION_EXIT_REASON,
} from '#/utils/auth/sessionExit';
import { clearAccess, setAccess } from '#/utils/auth/tokenStorage';
import { toast } from '#/utils/toast';
import {
  fetchMe,
  loginFailure,
  loginSuccess,
  meFailure,
  meSuccess,
  userLogin,
  userLogout,
} from '#/modules/infra/redux/infraSlice';

function extractAccessToken(data) {
  if (!data) {
    return null;
  }

  if (typeof data === 'string') {
    return data;
  }

  return data.accessToken ?? data.access_token ?? data.access ?? data.token ?? null;
}

function getApiErrorMessage(error, fallback) {
  const data = error.response?.data;
  return data?.message || error.message || fallback;
}

function resolveLoginErrorMessage(error) {
  return getApiErrorMessage(error, '登入時發生錯誤，請稍後再試');
}

function* handleFetchMe() {
  try {
    const result = yield call(fetchMeApi);
    const data = result?.data ?? {};
    yield put(meSuccess({
      user: data.user ?? null,
      roles: data.roles ?? [],
      permissions: data.permissions ?? [],
    }));
  } catch (error) {
    const message = getApiErrorMessage(error, '載入使用者權限失敗');
    yield put(meFailure({ message }));
  }
}

function* handleLogin(action) {
  try {
    const result = yield call(() => toast.promise(loginApi(action.payload), {
      pending: '登入中…',
      success: {
        render: ({ data }) => data?.message || '登入成功',
      },
      error: {
        render: ({ data }) => resolveLoginErrorMessage(data),
      },
    }));

    const access = extractAccessToken(result.data);

    if (!access) {
      throw new Error(result.message || '登入失敗，未取得 token');
    }

    setAccess(access, result.data?.expiresAt ?? null);
    resetSessionExitFlag();
    yield put(loginSuccess({
      access,
      user: result.data?.user ?? null,
      message: result.message,
    }));
    yield put(fetchMe());
  } catch (error) {
    clearAccess();
    const message = resolveLoginErrorMessage(error);
    yield put(loginFailure({ message }));
  }
}

function* handleLogout() {
  resetSessionExitFlag();
  try {
    yield call(logoutApi);
  } catch {
    // 後端 401／網路失敗仍本地結束 session（token 可能已失效）
  } finally {
    exitToLogin({
      reason: SESSION_EXIT_REASON.LOGOUT_SUCCESS,
    });
  }
}

export default function* infraSaga() {
  yield takeEvery(userLogin.type, handleLogin);
  yield takeEvery(userLogout.type, handleLogout);
  yield takeLeading(fetchMe.type, handleFetchMe);
  // Phase 2: refresh token queue stub
}
